
import { GenerateJigProgress } from '@/services/GenerateJigService'
import { Component, Vue } from 'vue-property-decorator'

type LottieAnimationInstance = {
  destroy: () => void
  stop: () => void
  goToAndStop: (value: number, isFrame: boolean) => void
  totalFrames: number
  currentFrame: number
  addEventListener?: (eventName: string, callback: () => void) => void
}

type LottieAnimationConfig = {
  container: HTMLElement
  renderer: string
  loop: boolean
  autoplay: boolean
  path: string
}

type LottieGlobal = {
  loadAnimation: (config: LottieAnimationConfig) => LottieAnimationInstance
}

@Component
export default class GenerateJigLoadingSection extends Vue {
  private loadingMessage: string = 'Preparing Jig generation...'
  private loadingPercentage: number = 0
  private loadingProgressAnimationFrame: number | null = null
  private displayedLoadingPercentage: number = 0
  private lottieAnimation: LottieAnimationInstance | null = null
  private lottieTotalFrames: number = 0
  private lottieSyncTimeoutIds: number[] = []

  private mounted() {
    this.startLottieAnimation()
  }

  private beforeDestroy() {
    this.stopLottieAnimation()
  }

  public updateProgress(progress: GenerateJigProgress) {
    this.loadingMessage = progress && progress.message ? progress.message : this.loadingMessage
    this.animateLoadingPercentageTo(progress && typeof progress.percentage === 'number' ? progress.percentage : this.loadingPercentage)
  }

  private getLottieContainer(): HTMLElement | null {
    const refs = this.$refs as { lottieContainer?: HTMLElement | HTMLElement[] }
    const containerRef = refs.lottieContainer

    if (!containerRef) {
      return null
    }

    if (Array.isArray(containerRef)) {
      if (containerRef.length === 0) {
        return null
      }
      return containerRef[0]
    }

    return containerRef
  }

  private clampPercentage(value: number): number {
    if (value < 0) {
      return 0
    }
    if (value > 100) {
      return 100
    }
    return Math.round(value)
  }

  private stopLoadingProgressAnimation() {
    if (this.loadingProgressAnimationFrame !== null) {
      window.cancelAnimationFrame(this.loadingProgressAnimationFrame)
      this.loadingProgressAnimationFrame = null
    }
  }

  private setLottieFrameFromPercentage(percentage: number) {
    if (!this.lottieAnimation || typeof this.lottieAnimation.goToAndStop !== 'function') {
      return
    }

    const totalFrames = this.lottieTotalFrames > 0 ? this.lottieTotalFrames : this.lottieAnimation.totalFrames || 0
    if (totalFrames <= 0) {
      return
    }

    const clamped = this.clampPercentage(percentage)
    const frame = (clamped / 100) * totalFrames
    this.lottieAnimation.goToAndStop(frame, true)
  }

  private clearLottieSyncTimeouts() {
    this.lottieSyncTimeoutIds.forEach((timeoutId: number) => {
      window.clearTimeout(timeoutId)
    })
    this.lottieSyncTimeoutIds = []
  }

  private scheduleLottieSync(callback: () => void, delayMs: number) {
    const timeoutId = window.setTimeout(() => {
      this.lottieSyncTimeoutIds = this.lottieSyncTimeoutIds.filter((id: number) => id !== timeoutId)
      callback()
    }, delayMs)

    this.lottieSyncTimeoutIds.push(timeoutId)
  }

  private animateLoadingPercentageTo(targetPercentage: number) {
    const nextTarget = this.clampPercentage(targetPercentage)
    this.stopLoadingProgressAnimation()

    const startValue = this.displayedLoadingPercentage
    const delta = nextTarget - startValue

    if (delta === 0) {
      this.loadingPercentage = nextTarget
      this.displayedLoadingPercentage = nextTarget
      this.setLottieFrameFromPercentage(nextTarget)
      return
    }

    if (delta < 0) {
      this.loadingPercentage = nextTarget
      this.displayedLoadingPercentage = nextTarget
      this.setLottieFrameFromPercentage(nextTarget)
      return
    }

    const duration = delta > 20 ? 350 : 220
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const value = startValue + delta * eased
      const roundedValue = this.clampPercentage(value)

      this.displayedLoadingPercentage = value
      this.loadingPercentage = roundedValue
      this.setLottieFrameFromPercentage(roundedValue)

      if (progress < 1) {
        this.loadingProgressAnimationFrame = window.requestAnimationFrame(animate)
        return
      }

      this.loadingProgressAnimationFrame = null
      this.displayedLoadingPercentage = nextTarget
      this.loadingPercentage = nextTarget
      this.setLottieFrameFromPercentage(nextTarget)
    }

    this.loadingProgressAnimationFrame = window.requestAnimationFrame(animate)
  }

  private startLottieAnimation() {
    const lottieHost = window as unknown as { lottie?: LottieGlobal }
    const container = this.getLottieContainer()

    if (!container) {
      return
    }

    this.stopLottieAnimation()
    container.innerHTML = ''
    this.lottieTotalFrames = 0

    if (!lottieHost.lottie || typeof lottieHost.lottie.loadAnimation !== 'function') {
      return
    }

    this.lottieAnimation = lottieHost.lottie.loadAnimation({
      container: container,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: '/img/lottie/loader.json',
    })

    const syncLottieFrames = () => {
      if (!this.lottieAnimation) {
        return
      }

      if (typeof this.lottieAnimation.totalFrames === 'number' && this.lottieAnimation.totalFrames > 0) {
        this.lottieTotalFrames = this.lottieAnimation.totalFrames - 1
        this.setLottieFrameFromPercentage(this.loadingPercentage)
      }
    }

    if (typeof this.lottieAnimation.addEventListener === 'function') {
      this.lottieAnimation.addEventListener('DOMLoaded', syncLottieFrames)
    }

    this.scheduleLottieSync(syncLottieFrames, 0)
    this.scheduleLottieSync(syncLottieFrames, 120)
  }

  private stopLottieAnimation() {
    const container = this.getLottieContainer()
    this.stopLoadingProgressAnimation()
    this.clearLottieSyncTimeouts()

    if (this.lottieAnimation && typeof this.lottieAnimation.destroy === 'function') {
      this.lottieAnimation.destroy()
    }

    this.lottieAnimation = null
    this.lottieTotalFrames = 0

    if (container) {
      container.innerHTML = ''
    }
  }
}
